import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  Plus,
  Filter,
  MoreHorizontal,
  Copy,
  Lock,
  Unlock,
  RefreshCw,
  FileDown,
  Eye,
  List,
  BarChart3,
  ArrowRight,
  Calendar as CalendarIcon,
  Trash,
  Pen,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Badge } from "../../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";

import { useToast } from "../../hooks/use-toast";
import { useNavigate } from "react-router-dom";

import OrganizationDetail from "./OrganizationDetail";
import { Skeleton } from "../../components/ui/skeleton";
import { Calendar } from "../../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";

import { DateRange } from "react-day-picker";
import {
  format,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
} from "date-fns";

import { makeFetchWithAuth } from "../../auth/authFetch";
import { fetchWithTimeout } from "../../utils/fetchWithTimeout";
import { toUserMessage } from "../../utils/toUserMessage";
import { fetchOrganizationLinkToken } from "../../hooks/organizations/organizationApi";
import {
  CreateOrganizationCommand,
  OrganizationDto,
  OrganizationsClient,
} from "../../web-api-client";
import { useOrganizations } from "../../hooks/organizations/useOrganizations";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import {
  useCreateOrganization,
  useCreateOrganizationLink,
} from "../../hooks/organizations/useOrganizationMutations";
import { useDeleteOrganization } from "../../hooks/organizations/useDeleteOrganization";
import { useUpdateOrganization } from "../../hooks/organizations/useOrganizationMutations";
import { UpdateOrganizationCommand } from "../../web-api-client";
import { Label } from "../../components/ui/label";
import Can from "../../auth/Can";
import { Perm } from "../../auth/permissions";
import { appConfig } from "../../appconfig";

const DEFAULT_PAGE_SIZE = 20;

type StatsPeriod = "week" | "month" | "quarter";

const formatDateParam = (date?: Date | null) =>
  date ? format(date, "yyyy-MM-dd") : undefined;

const getRangeForPeriod = (period: StatsPeriod) => {
  const now = new Date();
  switch (period) {
    case "month":
      return { from: startOfMonth(now), to: endOfMonth(now) };
    case "quarter":
      return { from: startOfQuarter(now), to: endOfQuarter(now) };
    case "week":
    default:
      return {
        from: startOfWeek(now, { weekStartsOn: 1 }),
        to: endOfWeek(now, { weekStartsOn: 1 }),
      };
  }
};

const buildRegistrationLink = (token: string) =>
  `${window.location.origin}?token=${token}`;

export default function OrganizationList() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [selectedStatsGroupId, setSelectedStatsGroupId] = useState<
    number | null
  >(null);

  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem("OrganizationActiveTab") ?? "management";
  });

  const [period, setPeriod] = useState<StatsPeriod>("week");
  const [dateRange, setDateRange] = useState<DateRange | undefined>();

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // token chỉ hiển thị 1 lần sau khi reset/tạo -> lưu tạm tại FE để copy
  const [tokenOverrides, setTokenOverrides] = useState<Record<number, string>>(
    {}
  );

  // Create Dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: "",
    code: "",
    contactName: "",
    contactPhone: "",
    note: "",
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<OrganizationDto | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    contactName: "",
    contactPhone: "",
    note: "",
  });

  const deleteOrganization = useDeleteOrganization();
  const updateOrganization = useUpdateOrganization();

  const orgQuery = useOrganizations({
    keyword: searchTerm,
    from: null,
    to: null,
    pageNumber,
    pageSize,
  });

  const createOrganization = useCreateOrganization();
  const createLink = useCreateOrganizationLink();

  const organizations = orgQuery.data?.items ?? [];
  const totalCount = orgQuery.data?.totalCount ?? 0;

  const isLoading = useMemo(() => {
    return (
      orgQuery.isLoading ||
      createOrganization.isPending ||
      createLink.isPending ||
      deleteOrganization.isPending ||
      updateOrganization.isPending
    );
  }, [orgQuery, createOrganization, createLink, deleteOrganization]);

  const authFetch = useMemo(
    () =>
      makeFetchWithAuth(fetchWithTimeout(), {
        onUnauthorized: () =>
          navigate("/login", {
            replace: true,
            state: { from: window.location.pathname },
          }),
      }),
    [navigate]
  );

  const organizationsClient = useMemo(
    () => new OrganizationsClient(appConfig.apiBaseUrl, { fetch: authFetch }),
    [authFetch]
  );

  const resolvedRange = useMemo(() => {
    if (dateRange?.from && dateRange?.to) {
      return { from: dateRange.from, to: dateRange.to };
    }
    return getRangeForPeriod(period);
  }, [dateRange, period]);

  const dateRangeLabel = useMemo(() => {
    if (dateRange?.from) {
      return dateRange.to
        ? `${format(dateRange.from, "dd/MM/yyyy")} - ${format(
            dateRange.to,
            "dd/MM/yyyy"
          )}`
        : format(dateRange.from, "dd/MM/yyyy");
    }

    return `${format(resolvedRange.from, "dd/MM/yyyy")} - ${format(
      resolvedRange.to,
      "dd/MM/yyyy"
    )}`;
  }, [dateRange, resolvedRange]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const filteredOrgs = useMemo(() => {
    return organizations.filter((org) => {
      if (statusFilter === "enabled") return org.link?.isEnabled;
      if (statusFilter === "disabled") return org.link && !org.link.isEnabled;
      return true;
    });
  }, [organizations, statusFilter]);

  const activeStatsGroup = selectedStatsGroupId
    ? organizations.find((g) => g.id === selectedStatsGroupId) ?? null
    : organizations.length > 0
    ? organizations[0]
    : null;

  // giữ tên cũ để khỏi sửa nhiều chỗ
  const fetchOrganizations = orgQuery.refetch;

  useEffect(() => {
    if (!selectedStatsGroupId && organizations.length > 0) {
      setSelectedStatsGroupId(organizations[0].id ?? null);
    }
  }, [organizations, selectedStatsGroupId]);

  useEffect(() => {
    localStorage.setItem("OrganizationActiveTab", activeTab);
  }, [activeTab]);

  const copyLink = async (org: OrganizationDto) => {
    const orgId = org?.id ?? 0;
    let token = tokenOverrides[orgId];
    if (!token && orgId > 0) {
      try {
        const response = await fetchOrganizationLinkToken(orgId);
        if (response.token) {
          token = response.token;
          setTokenOverrides((prev) => ({ ...prev, [orgId]: response.token! }));
        }
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Không lấy được token",
          description: toUserMessage(error),
        });
        return;
      }
    }

    if (!token) {
      toast({
        variant: "destructive",
        title: "Chưa có token",
        description: "Vui lòng reset hoặc tạo link để lấy token hợp lệ.",
      });
      return;
    }

    const link = buildRegistrationLink(token);
    await navigator.clipboard.writeText(link);

    toast({
      title: "Đã sao chép",
      description: "Link đăng ký đã được sao chép vào clipboard.",
    });
  };

  const handleCreateOrganization = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    if (!createForm.name.trim()) {
      toast({
        variant: "destructive",
        title: "Thiếu thông tin",
        description: "Vui lòng nhập tên nhóm.",
      });
      return;
    }

    try {
      const created = await createOrganization.mutateAsync(
        new CreateOrganizationCommand({
          name: createForm.name.trim(),
          code: undefined,
          contactName: createForm.contactName.trim() || undefined,
          contactPhone: createForm.contactPhone.trim() || undefined,
          note: createForm.note.trim() || undefined,
        })
      );

      // Tạo link ngay
      await createLink.mutateAsync(created.id!);

      toast({
        variant: "success",
        title: "Tạo nhóm thành công",
        description: "Nhóm tổ chức mới đã được tạo.",
      });
      setCreateDialogOpen(false);
      setCreateForm({
        name: "",
        code: "",
        contactName: "",
        contactPhone: "",
        note: "",
      });
    } catch (error) {
      const errMsg = await toUserMessage(error);
      toast({
        variant: "destructive",
        title: "Tạo nhóm thất bại",
        description: errMsg,
      });
    }
  };

  const handleDeleteClick = (organization: OrganizationDto) => {
    setTimeout(() => {
      setSelectedOrg(organization);
      setDeleteDialogOpen(true);
    }, 100);
  };

  const handleUpdateClick = (organization: OrganizationDto) => {
    setTimeout(() => {
      setSelectedOrg(organization);
      setFormData({
        name: organization.name!,
        code: organization.code ?? "",
        contactName: organization.contactName ?? "",
        contactPhone: organization.contactPhone ?? "",
        note: organization.note ?? "",
      });
      setUpdateDialogOpen(true);
    }, 100);
  };

  const handleConfirmDelete = async () => {
    if (!selectedOrg) return;

    try {
      deleteOrganization.mutate(selectedOrg.id!, {
        onSuccess: (result) => {
          setDeleteDialogOpen(false);
          if (result.mode === "HardDeleted") {
            toast({
              variant: "success" as any,
              title: "Đã xóa",
              description: "",
            });
          } else {
            toast({
              variant: "success" as any,
              title: "Đã lưu trữ",
              description: "",
            });
          }
        },
      });
    } catch (error) {
      const errMsg = await toUserMessage(error);
      toast({
        variant: "destructive",
        title: "Thao tác xóa thất bại",
        description: errMsg,
      });
    }
  };

  const handleConfirmUpdate = async () => {
    if (!selectedOrg) return;

    try {
      updateOrganization.mutate(
        {
          id: selectedOrg.id!,
          payload: new UpdateOrganizationCommand({
            name: formData.name,
            code: undefined,
            contactName: formData.contactName || undefined,
            contactPhone: formData.contactPhone || undefined,
            note: formData.note || undefined,
          }),
        },
        {
          onSuccess: () => {
            setUpdateDialogOpen(false);
            toast({
              variant: "success",
              title: "Đã cập nhật",
              description: "Thông tin tổ chức đã được lưu.",
            });
          },
        }
      );
    } catch (error) {
      const errMsg = await toUserMessage(error);
      toast({
        variant: "destructive",
        title: "Thao tác cập nhật thất bại",
        description: errMsg,
      });
    }
  };

  const handleResetLink = async (orgId: number) => {
    try {
      const result = await organizationsClient.postApiOrganizationsLinkReset(
        orgId
      );
      if (result?.token) {
        setTokenOverrides((prev) => ({ ...prev, [orgId]: result.token }));
      }
      await fetchOrganizations();

      toast({
        // nếu project bạn không support "success" thì đổi thành bỏ variant hoặc dùng "default"
        variant: "success" as any,
        title: "Đã reset link",
        description: "Token mới chỉ hiển thị một lần, vui lòng lưu lại.",
      });
    } catch (error: any) {
      const message = await toUserMessage(error);
      toast({
        variant: "destructive",
        title: "Reset link thất bại",
        description: message,
      });
    }
  };

  const handleToggleLink = async (orgId: number, enabled: boolean) => {
    try {
      if (enabled) {
        await organizationsClient.patchApiOrganizationsLinkEnable(orgId);
      } else {
        await organizationsClient.patchApiOrganizationsLinkDisable(orgId);
      }
      await fetchOrganizations();
    } catch (error: any) {
      const message = await toUserMessage(error);
      toast({
        variant: "destructive",
        title: "Cập nhật trạng thái thất bại",
        description: message,
      });
    }
  };

  const renderLinkDisplay = (org: OrganizationDto) => {
    const token = tokenOverrides[org?.id ?? 0];
    if (token) return buildRegistrationLink(token);
    if (org.link?.tokenPrefix) return `${org.link.tokenPrefix}`;
    return "Chưa có link";
  };

  return (
    <>
      <div className="border-b-2 border-green-600 p-4 bg-white flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-jira text-2xl leading-8 font-semibold tracking-tight text-slate-900">
            Đăng ký theo tổ chức
          </h1>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-6 mt-4"
      >
        <TabsList className="grid w-full md:w-[400px] grid-cols-2">
          <TabsTrigger
            value="management"
            className="gap-2 p-4 bg-white data-[state=active]:border-b-2 data-[state=active]:border-b-green-600"
          >
            <List className="w-4 h-4" /> Quản lý nhóm
          </TabsTrigger>
          <TabsTrigger
            value="reports"
            className="gap-2 p-4 bg-white data-[state=active]:border-b-2 data-[state=active]:border-b-green-600 border-b-gray-300"
          >
            <BarChart3 className="w-4 h-4" /> Thống kê & Báo cáo
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: danh sách */}
        <TabsContent value="management" className="space-y-6">
          <Card>
            <CardHeader className="pb-3 flex justify-between flex-row flex-wrap gap-4">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 max-w-lg w-full">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-black-500" />
                  <Input
                    placeholder="Tìm kiếm..."
                    className="pl-9"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setPageNumber(1);
                    }}
                  />
                </div>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[180px]">
                    <div className="flex items-center gap-2">
                      <Filter className="w-4 h-4 text-black-500" />
                      <SelectValue placeholder="Status" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Trạng thái</SelectItem>
                    <SelectItem value="enabled">Đang mở</SelectItem>
                    <SelectItem value="disabled">Đã khóa</SelectItem>
                  </SelectContent>
                </Select>

                {/* <Select value={period} onValueChange={(v) => setPeriod(v as StatsPeriod)}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Kỳ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="week">Tuần</SelectItem>
                    <SelectItem value="month">Tháng</SelectItem>
                    <SelectItem value="quarter">Quý</SelectItem>
                  </SelectContent>
                </Select>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn("justify-start text-left font-normal", !dateRange?.from && "text-black-500")}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateRangeLabel}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="range"
                      selected={dateRange}
                      onSelect={(range) => {
                        setDateRange(range);
                        setPageNumber(1);
                      }}
                      numberOfMonths={2}
                    />
                  </PopoverContent>
                </Popover> */}
              </div>

              <Can perm={Perm.Organization_Manage}>
                <div className="flex items-center gap-2">
                  {/* <Button variant="outline">
                  <FileDown className="mr-2 h-4 w-4" />
                  Xuất dữ liệu
                </Button> */}
                  <Dialog
                    open={createDialogOpen}
                    onOpenChange={(open) => {
                      setCreateDialogOpen(open);
                      if (!open) {
                        setCreateForm({
                          name: "",
                          code: "",
                          contactName: "",
                          contactPhone: "",
                          note: "",
                        });
                      }
                    }}
                  >
                    <DialogTrigger asChild>
                      <Button className="bg-primary hover:bg-primary/90 text-white shadow-sm">
                        <Plus className="mr-2 h-4 w-4" />
                        Tạo nhóm mới
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle>Tạo nhóm mới</DialogTitle>
                        <DialogDescription>
                          Nhập thông tin cơ bản cho tổ chức mới.
                        </DialogDescription>
                      </DialogHeader>
                      <form
                        className="space-y-4"
                        onSubmit={handleCreateOrganization}
                      >
                        <div className="space-y-2">
                          <label className="text-sm font-medium">
                            Tên nhóm <span className="text-red-500">*</span>
                          </label>
                          <Input
                            value={createForm.name}
                            onChange={(event) =>
                              setCreateForm((prev) => ({
                                ...prev,
                                name: event.target.value,
                              }))
                            }
                            placeholder="Ví dụ: Công ty ABC"
                            required
                          />
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="space-y-2">
                            <label className="text-sm font-medium">
                              Người liên hệ
                            </label>
                            <Input
                              value={createForm.contactName}
                              onChange={(event) =>
                                setCreateForm((prev) => ({
                                  ...prev,
                                  contactName: event.target.value,
                                }))
                              }
                              placeholder="Nguyễn Văn A"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-medium">
                              Số điện thoại
                            </label>
                            <Input
                              value={createForm.contactPhone}
                              onChange={(event) =>
                                setCreateForm((prev) => ({
                                  ...prev,
                                  contactPhone: event.target.value,
                                }))
                              }
                              placeholder="0901 234 567"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Ghi chú</label>
                          <Textarea
                            value={createForm.note}
                            onChange={(event) =>
                              setCreateForm((prev) => ({
                                ...prev,
                                note: event.target.value,
                              }))
                            }
                            placeholder="Thông tin thêm về nhóm..."
                          />
                        </div>
                        <DialogFooter className="gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setCreateDialogOpen(false)}
                          >
                            Hủy
                          </Button>
                          <Button
                            type="submit"
                            className="bg-primary hover:bg-primary/90 text-white"
                            disabled={createOrganization.isPending}
                          >
                            {createOrganization.isPending
                              ? "Đang tạo..."
                              : "Tạo nhóm"}
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </Can>
            </CardHeader>

            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[250px] truncate">
                        Tổ chức
                      </TableHead>
                      <TableHead className="text-left truncate">
                        Người liên hệ
                      </TableHead>
                      <TableHead className="text-left truncate">
                        Số điện thoại
                      </TableHead>
                      <TableHead className="truncate">Link đăng ký</TableHead>
                      <TableHead className="text-center truncate">
                        Trạng thái
                      </TableHead>
                      <TableHead className="text-center truncate">
                        Tổng đăng ký
                      </TableHead>
                      <TableHead className="text-center truncate">
                        Đã xác nhận
                      </TableHead>
                      <TableHead className="text-center truncate">
                        Đã hủy
                      </TableHead>
                      <Can perm={Perm.Organization_Manage}>
                        <TableHead className="w-[50px]"></TableHead>
                      </Can>
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {isLoading ? (
                      Array.from({ length: 4 }).map((_, rowIndex) => (
                        <TableRow key={`skeleton-row-${rowIndex}`}>
                          {Array.from({ length: 9 }).map((_, colIndex) => (
                            <TableCell
                              key={`skeleton-cell-${rowIndex}-${colIndex}`}
                            >
                              <Skeleton className="h-10 w-full" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : filteredOrgs.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={9}
                          className="text-center text-black-500"
                        >
                          Không có dữ liệu phù hợp
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredOrgs.map((org) => (
                        <TableRow
                          className="cursor-pointer"
                          key={org.id}
                          onClick={() => {
                            setSelectedStatsGroupId(org?.id ?? null);
                            setActiveTab("reports");
                          }}
                        >
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium text-foreground">
                                {org.name}
                              </span>
                            </div>
                          </TableCell>

                          <TableCell>
                            <span className="font-medium text-foreground">
                              {org.contactName ?? "--"}
                            </span>
                          </TableCell>

                          <TableCell>
                            <span className="font-medium text-foreground">
                              {org.contactPhone ?? "--"}
                            </span>
                          </TableCell>

                          <TableCell className="max-w-[250px]">
                            <div className="flex items-center gap-2">
                              <code className="bg-muted px-2 py-1 rounded text-xs font-mono text-black-500 truncate ">
                                {renderLinkDisplay(org)}
                              </code>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={(e) => {
                                  copyLink(org);
                                  e.stopPropagation();
                                }}
                                disabled={!org.link}
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>

                          <TableCell className="text-center">
                            <Badge
                              variant={
                                org.link?.isEnabled ? "default" : "secondary"
                              }
                              className={
                                org.link?.isEnabled
                                  ? "bg-green-100 text-green-700 hover:bg-green-100 border-green-200 truncate"
                                  : "bg-gray-100 text-gray-700 hover:bg-gray-100 border-gray-200 truncate"
                              }
                            >
                              {org.link?.isEnabled
                                ? "Đang hoạt động"
                                : "Đã khóa"}
                            </Badge>
                          </TableCell>

                          <TableCell className="text-center font-medium">
                            {org.counters?.total ?? 0}
                          </TableCell>

                          <TableCell className="text-center">
                            <span className="text-green-600 font-medium">
                              {org.counters?.confirmed ?? 0}
                            </span>
                            <span className="text-black-500 text-xs ml-1">
                              (
                              {Math.round(
                                ((org.counters?.confirmed ?? 0) /
                                  (org.counters?.total || 1)) *
                                  100
                              )}
                              %)
                            </span>
                          </TableCell>

                          <TableCell className="text-center">
                            <span className="text-red-600 font-medium">
                              {org.counters?.cancelled ?? 0}
                            </span>
                            <span className="text-black-500 text-xs ml-1">
                              (
                              {Math.round(
                                ((org.counters?.cancelled ?? 0) /
                                  (org.counters?.total || 1)) *
                                  100
                              )}
                              %)
                            </span>
                          </TableCell>

                          <Can perm={Perm.Organization_Manage}>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>

                                <DropdownMenuContent
                                  align="end"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <DropdownMenuLabel>
                                    Thao tác
                                  </DropdownMenuLabel>

                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    onClick={() => {
                                      setSelectedStatsGroupId(org?.id ?? null);
                                      setActiveTab("reports");
                                    }}
                                  >
                                    <Eye className="mr-2 h-4 w-4" /> Xem chi
                                    tiết
                                  </DropdownMenuItem>

                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    onClick={() => handleUpdateClick(org)}
                                  >
                                    <Pen className="mr-2 h-4 w-4" />
                                    Cập nhật
                                  </DropdownMenuItem>

                                  <DropdownMenuSeparator />

                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    onClick={() =>
                                      handleToggleLink(
                                        org?.id ?? 0,
                                        !org.link?.isEnabled
                                      )
                                    }
                                    disabled={isLoading}
                                  >
                                    {org.link?.isEnabled ? (
                                      <>
                                        <Lock className="mr-2 h-4 w-4" />
                                        Khóa đăng ký
                                      </>
                                    ) : (
                                      <>
                                        <Unlock className="mr-2 h-4 w-4" />
                                        Mở đăng ký
                                      </>
                                    )}
                                  </DropdownMenuItem>

                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    onClick={() =>
                                      handleResetLink(org?.id ?? -1)
                                    }
                                    disabled={isLoading}
                                  >
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    Đổi Link
                                  </DropdownMenuItem>

                                  <DropdownMenuSeparator />

                                  <Dialog
                                    open={deleteDialogOpen}
                                    onOpenChange={setDeleteDialogOpen}
                                  >
                                    <DialogContent>
                                      <DialogHeader>
                                        <DialogTitle>Xóa tổ chức?</DialogTitle>
                                        <DialogDescription>
                                          Nếu tổ chức đã có đăng ký, hệ thống sẽ
                                          lưu trữ thay vì xóa.
                                        </DialogDescription>
                                      </DialogHeader>
                                      <DialogFooter className="gap-2">
                                        <Button
                                          variant="outline"
                                          onClick={() =>
                                            setDeleteDialogOpen(false)
                                          }
                                          disabled={isLoading}
                                        >
                                          Hủy
                                        </Button>
                                        <Button
                                          className="bg-red-600 hover:bg-red-700 text-white"
                                          onClick={handleConfirmDelete}
                                          disabled={isLoading}
                                        >
                                          Xóa
                                        </Button>
                                      </DialogFooter>
                                    </DialogContent>
                                  </Dialog>
                                  <DropdownMenuItem
                                    className="text-red-600 focus:text-red-600 cursor-pointer"
                                    onClick={() => handleDeleteClick(org)}
                                    disabled={isLoading}
                                  >
                                    <Trash className="mr-2 h-4 w-4" />
                                    Xóa
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </Can>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-border">
                <div className="flex items-center gap-2 text-sm text-black-500">
                  <span>Hiển thị</span>
                  <Select
                    value={String(pageSize)}
                    onValueChange={(val) => {
                      setPageSize(Number(val));
                      setPageNumber(1);
                    }}
                  >
                    <SelectTrigger className="w-[70px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                    </SelectContent>
                  </Select>
                  <span>trong {totalCount} kết quả</span>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                    disabled={pageNumber === 1 || isLoading}
                  >
                    Trước
                  </Button>
                  <span className="text-sm text-black-500 px-2">
                    Trang {pageNumber} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPageNumber((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={pageNumber >= totalPages || isLoading}
                  >
                    Sau
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 2: báo cáo */}
        <TabsContent value="reports" className="space-y-6 bg-white p-6">
          <div className="min-w-0 grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-3 space-y-4">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-black-500" />
                <Input
                  placeholder="Tìm nhóm..."
                  className="pl-9"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPageNumber(1);
                  }}
                />
              </div>

              <div className="space-y-1 md:max-h-[600px] max-h-[200px] overflow-y-auto pr-2">
                {filteredOrgs.map((group) => (
                  <button
                    key={group.id}
                    onClick={() => setSelectedStatsGroupId(group?.id ?? null)}
                    className={`w-full text-left px-3 py-3 rounded-lg text-sm transition-colors flex items-center justify-between group ${
                      activeStatsGroup?.id === group.id
                        ? "bg-primary text-primary-foreground shadow-md"
                        : "hover:bg-muted text-black-500 hover:text-foreground border border-transparent hover:border-border"
                    }`}
                  >
                    <span className="font-medium truncate">{group.name}</span>
                    <ArrowRight
                      className={`w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity ${
                        activeStatsGroup?.id === group.id ? "opacity-100" : ""
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="md:col-span-9 w-full h-full">
              <OrganizationDetail
                organization={activeStatsGroup}
                from={resolvedRange.from}
                to={resolvedRange.to}
                onResetLink={handleResetLink}
                tokenOverrides={tokenOverrides}
                onTokenResolved={(orgId, token) =>
                  setTokenOverrides((prev) => ({ ...prev, [orgId]: token }))
                }
                onToggleLink={handleToggleLink}
                onRefresh={fetchOrganizations}
              />
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cập nhật tổ chức</DialogTitle>
            <DialogDescription>
              Chỉnh sửa thông tin tổ chức trước khi lưu.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tên tổ chức</Label>
              <Input
                value={formData.name}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    name: event.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Người liên hệ</Label>
              <Input
                value={formData.contactName}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    contactName: event.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Số điện thoại</Label>
              <Input
                value={formData.contactPhone}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    contactPhone: event.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Ghi chú</Label>
              <textarea
                className="flex min-h-[120px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-black-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                value={formData.note}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    note: event.target.value,
                  }))
                }
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setUpdateDialogOpen(false)}
              disabled={isLoading}
            >
              Hủy
            </Button>
            <Button onClick={handleConfirmUpdate} disabled={isLoading}>
              Lưu thay đổi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Xóa tổ chức?</DialogTitle>
            <DialogDescription>
              Nếu tổ chức đã có đăng ký, hệ thống sẽ lưu trữ thay vì xóa.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isLoading}
            >
              Hủy
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleConfirmDelete}
              disabled={isLoading}
            >
              Xóa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <></>
    </>
  );
}
